import { useState, useEffect } from "react";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { db } from "../../lib/firebase";
import { useAuthContext } from "../../contexts/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import Card from "../../components/Card";
import { Wrench, Save, AlertCircle, Home, Power, PowerOff, ArrowLeft } from "lucide-react";

interface MaintenanceSettings {
  enabled: boolean;
  message: string;
  updatedAt: Date;
  updatedBy: string;
}

export default function MaintenanceControl() {
  const { user, loading: authLoading } = useAuthContext();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [message, setMessage] = useState("ขณะนี้ระบบ EduMi อยู่ระหว่างปรับปรุงชั่วคราว\nทีมงานกำลังอัปเดตระบบ กรุณากลับมาใหม่อีกครั้ง");
  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    if (authLoading) return;
    
    if (user?.email !== "viitamint@gmail.com") {
      navigate("/");
      return;
    }
    loadSettings();
  }, [user, authLoading, navigate]);

  const loadSettings = async () => {
    try {
      const docRef = doc(db, "system", "maintenance");
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data() as MaintenanceSettings;
        setEnabled(data.enabled || false);
        setMessage(data.message || "ขณะนี้ระบบ EduMi อยู่ระหว่างปรับปรุงชั่วคราว\nทีมงานกำลังอัปเดตระบบ กรุณากลับมาใหม่อีกครั้ง");
      }
    } catch (error) {
      console.error("Error loading maintenance settings:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!user) return;
    
    setSaving(true);
    setSuccessMessage("");
    
    try {
      const docRef = doc(db, "system", "maintenance");
      await setDoc(docRef, {
        enabled,
        message,
        updatedAt: new Date(),
        updatedBy: user.email,
      });
      
      setSuccessMessage("บันทึกการตั้งค่าสำเร็จ!");
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      console.error("Error saving maintenance settings:", error);
      alert("เกิดข้อผิดพลาดในการบันทึก");
    } finally {
      setSaving(false);
    }
  };

  const toggleMaintenance = async () => {
    const newState = !enabled;
    setEnabled(newState);
    
    if (!user) return;
    
    try {
      const docRef = doc(db, "system", "maintenance");
      await setDoc(docRef, {
        enabled: newState,
        message,
        updatedAt: new Date(),
        updatedBy: user.email,
      });
      
      setSuccessMessage(newState ? "เปิดโหมดปรับปรุงแล้ว!" : "ปิดโหมดปรับปรุงแล้ว!");
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      console.error("Error toggling maintenance:", error);
      setEnabled(!newState);
      alert("เกิดข้อผิดพลาดในการเปลี่ยนสถานะ");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-soft">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-primary mx-auto mb-4"></div>
          <p className="text-textLight text-lg">กำลังโหลด...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-soft">
      {/* Header */}
      <div className="bg-gradient-primary text-white py-8 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <Link to="/admin">
                <button className="p-2 hover:bg-white/20 rounded-lg transition-colors">
                  <ArrowLeft className="w-6 h-6 text-white" />
                </button>
              </Link>
              <Wrench className="w-10 h-10 text-white" />
              <h1 className="text-4xl font-bold">Maintenance Mode</h1>
            </div>
            <Link to="/">
              <button className="flex items-center gap-2 px-6 py-3 bg-white/20 hover:bg-white/30 rounded-xl font-semibold transition-all backdrop-blur-sm border border-white/30">
                <Home className="w-5 h-5" />
                <span>ไปหน้าแรก</span>
              </button>
            </Link>
          </div>
          <p className="text-white/90">จัดการโหมดปรับปรุงระบบ</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto py-8 px-4">
        {/* Success Message */}
        {successMessage && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
              <Save className="w-5 h-5 text-green-600" />
            </div>
            <p className="text-green-800 font-semibold">{successMessage}</p>
          </div>
        )}

        {/* Status Card */}
        <Card className="mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={`w-16 h-16 rounded-2xl flex items-center justify-center ${
                enabled ? "bg-red-100" : "bg-green-100"
              }`}>
                {enabled ? (
                  <PowerOff className="w-8 h-8 text-red-600" />
                ) : (
                  <Power className="w-8 h-8 text-green-600" />
                )}
              </div>
              <div>
                <h2 className="text-2xl font-bold text-textDark">
                  สถานะปัจจุบัน
                </h2>
                <p className={`text-lg font-semibold ${
                  enabled ? "text-red-600" : "text-green-600"
                }`}>
                  {enabled ? "🔴 กำลังปรับปรุง" : "🟢 เปิดให้บริการปกติ"}
                </p>
              </div>
            </div>
            
            <button
              onClick={toggleMaintenance}
              className={`px-8 py-4 rounded-xl font-bold text-lg transition-all ${
                enabled
                  ? "bg-green-500 hover:bg-green-600 text-white"
                  : "bg-red-500 hover:bg-red-600 text-white"
              }`}
            >
              {enabled ? "เปิดให้บริการ" : "ปิดปรับปรุง"}
            </button>
          </div>
        </Card>

        {/* Alert Info */}
        <div className="mb-6 bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3">
          <AlertCircle className="w-6 h-6 text-blue-600 mt-0.5" />
          <div className="flex-1">
            <p className="text-blue-900 font-semibold mb-1">
              ข้อมูลสำคัญ
            </p>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• เมื่อเปิดโหมดปรับปรุง ผู้ใช้ทั่วไปจะไม่สามารถเข้าใช้งานเว็บได้</li>
              <li>• Admin ยังสามารถเข้าใช้งานได้ตามปกติ</li>
              <li>• ข้อความที่ตั้งค่าจะแสดงบนหน้า Maintenance</li>
              <li>• การเปลี่ยนแปลงจะมีผลทันที</li>
            </ul>
          </div>
        </div>

        {/* Message Editor */}
        <Card>
          <div className="mb-6">
            <label className="block text-lg font-bold text-textDark mb-2">
              ข้อความแจ้งผู้ใช้
            </label>
            <p className="text-sm text-textLight mb-4">
              ข้อความนี้จะแสดงบนหน้า Maintenance เมื่อเปิดโหมดปรับปรุง
            </p>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={6}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none text-textDark"
              placeholder="ใส่ข้อความที่ต้องการแจ้งผู้ใช้..."
            />
          </div>

          <button
            onClick={handleSave}
            disabled={saving}
            className="w-full bg-primary hover:bg-primary/90 text-white font-bold py-4 rounded-xl transition-all flex items-center justify-center gap-2 disabled:opacity-50"
          >
            <Save className="w-5 h-5" />
            {saving ? "กำลังบันทึก..." : "บันทึกข้อความ"}
          </button>
        </Card>

        {/* Preview */}
        <Card className="mt-6">
          <h3 className="text-lg font-bold text-textDark mb-4">
            ตัวอย่างหน้า Maintenance
          </h3>
          <div className="bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-100 rounded-xl p-8">
            <div className="bg-white rounded-2xl shadow-xl p-8 text-center max-w-md mx-auto">
              <div className="flex justify-center mb-6">
                <div className="bg-blue-100 p-5 rounded-full animate-pulse">
                  <Wrench className="w-12 h-12 text-blue-600" />
                </div>
              </div>
              <div className="text-gray-900 whitespace-pre-line">
                {message}
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
